/**
 * THE page-clamp rule for a client-paginated list (root `CLAUDE.md`'s
 * `### Pagination` section): `totalPages` is always >= 1, and the page a
 * caller is actually shown is clamped to it — `page = min(currentPage,
 * totalPages)` — so a refetch that SHRINKS the filtered set can never strand
 * the view past its last page. Mirrors `hooks/useHireTableList.js`'s inline
 * version (`effectivePageSize`/`totalPages`/`page`, :167-178), extracted
 * 2026-09-28 once `pages/DepartmentOrders.js` and `pages/MyOrders.js` both
 * needed it verbatim — `useHireTableList.js` itself is left untouched (a
 * third existing instance, out of scope for this change; a caller may
 * migrate it later).
 *
 * 🔑 **A DERIVED value, not a `useEffect`.** Both callers compute `page` in
 * the same render as `totalPages` and use it for BOTH the slice and the
 * `Pagination` component's `currentPage` prop — so a shrinking refetch is
 * correct on the very next render with no intermediate frame showing a
 * blank page under a stale pager. An effect that writes the clamp back into
 * `currentPage` state was deliberately NOT added: both pages already have
 * `currentPage`-writing effects with a documented, order-sensitive
 * relationship to a deep-link "jump forward" effect (search each page's own
 * docblock for "MUST STAY DECLARED AFTER"), and a third effect racing that
 * ordering is a real hazard the derived value avoids entirely. If a caller
 * later needs `currentPage` STATE itself to reflect the clamp (nothing here
 * does), sync it separately and keep correctness resting on the derived
 * value, never on the effect.
 *
 * `pageSize === 'ALL'` is handled the same way root `CLAUDE.md`'s Pagination
 * section requires for arithmetic generally: there is exactly one page.
 */

/** `totalPages` for `itemCount` items at `pageSize` per page. Always >= 1. */
export function computeTotalPages(itemCount, pageSize) {
  if (pageSize === 'ALL') return 1;
  return Math.max(1, Math.ceil(itemCount / pageSize));
}

/** The page actually shown: `currentPage`, clamped to `totalPages`. */
export function clampPage(currentPage, totalPages) {
  return Math.min(currentPage, totalPages);
}
